import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import jsPDF from "jspdf";
import toast from "react-hot-toast";
import SelectMenu from "../components/crm/SelectMenu";
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Calendar as CalendarIcon,
  ChevronDown,
  SlidersHorizontal,
  FileText,
  FileSpreadsheet,
  FileType2,
  Download,
  Eye,
  CheckCircle2,
  Loader2,
  ClipboardList,
} from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_WHITE, ACTION_BTN_ICON } from "../styles/actionButtons";

// ---- date helpers (kept from the original) ----
const formatDateForFirestore = (date) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
const parseDateFromString = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split("/");
  if (!day || !month || !year) return null;
  return new Date(`${year}-${month}-${day}`);
};
const fmtLong = (d) =>
  d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtWeekday = (d) => (d ? d.toLocaleDateString("en-GB", { weekday: "long" }) : "");
const escapeHtml = (s) =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const STEPS = [
  { n: 1, label: "Select Date" },
  { n: 2, label: "Filters (Optional)" },
  { n: 3, label: "Preview" },
  { n: 4, label: "Export" },
];

const FORMATS = [
  { key: "pdf", label: "PDF", Icon: FileText, tint: "bg-red-50 text-red-500", hint: "Best for printing and sharing" },
  { key: "excel", label: "Excel", Icon: FileSpreadsheet, tint: "bg-emerald-50 text-emerald-600", hint: "Best for data analysis" },
  { key: "csv", label: "CSV", Icon: FileType2, tint: "bg-violet-50 text-violet-600", hint: "Best for importing to other systems" },
];

const ExportCauseList = () => {
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const uid = globalUser?.uid;
  const pendingCollection = useMemo(() => collection(db, "pending"), []);

  const [step, setStep] = useState(1); // 1..4, 5 = success
  const [dateMode, setDateMode] = useState("single"); // single | range
  const [selectedDate, setSelectedDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Real, data-backed filters (from the fields Add Case actually captures).
  const [courtFilter, setCourtFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [advocateFilter, setAdvocateFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [showMore, setShowMore] = useState(false);

  const [format, setFormat] = useState("pdf");
  const [exporting, setExporting] = useState(false);
  const [exportedFile, setExportedFile] = useState(null);

  // ---- fetch (kept: user's pending cases with a hearing on the date/range) ----
  const fetchCasesByDate = useCallback(
    async (date) => {
      if (!uid || !date) return [];
      const formattedDate = formatDateForFirestore(date);
      const snapshot = await getDocs(
        query(pendingCollection, where("owner", "==", uid), where("nextHearingDate", "==", formattedDate))
      );
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    },
    [pendingCollection, uid]
  );
  const fetchCasesByRange = useCallback(
    async (start, end) => {
      if (!uid || !start || !end) return [];
      const snapshot = await getDocs(query(pendingCollection, where("owner", "==", uid)));
      return snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => {
          const cd = parseDateFromString(c.nextHearingDate);
          return cd && cd >= start && cd <= end;
        });
    },
    [pendingCollection, uid]
  );

  const loadCases = async () => {
    setIsLoading(true);
    try {
      const result =
        dateMode === "single"
          ? await fetchCasesByDate(selectedDate)
          : await fetchCasesByRange(startDate, endDate);
      setCases(result);
      return result;
    } catch (e) {
      console.error("fetch cause list:", e);
      toast.error("Unable to fetch cases for the selected date.");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // ---- filter option lists (from fetched cases) ----
  const uniq = (vals) => {
    const s = new Set();
    vals.forEach((v) => {
      const t = (v || "").toString().trim();
      if (t) s.add(t);
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  };
  const courtOptions = useMemo(() => uniq(cases.map((c) => c.courtName || c.court)), [cases]);
  const typeOptions = useMemo(() => uniq(cases.map((c) => c.caseType)), [cases]);
  const stageOptions = useMemo(() => uniq(cases.map((c) => c.caseStage || c.stage)), [cases]);
  const yearOptions = useMemo(() => uniq(cases.map((c) => c.caseYear)), [cases]);
  const advocateOptions = useMemo(() => {
    const s = new Set();
    cases.forEach((c) => {
      [c.petitionerAdvocate, c.respondentAdvocate].forEach((a) => a && a.trim() && s.add(a.trim()));
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [cases]);

  const caseMatchesAdvocate = (c, adv) =>
    [c.petitionerAdvocate, c.respondentAdvocate].some((n) => (n || "").trim() === adv);

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (courtFilter !== "all" && (c.courtName || c.court || "").trim() !== courtFilter) return false;
      if (typeFilter !== "all" && (c.caseType || "").trim() !== typeFilter) return false;
      if (advocateFilter !== "all" && !caseMatchesAdvocate(c, advocateFilter)) return false;
      if (stageFilter !== "all" && ((c.caseStage || c.stage || "").trim() !== stageFilter)) return false;
      if (yearFilter !== "all" && String(c.caseYear || "").trim() !== yearFilter) return false;
      return true;
    });
  }, [cases, courtFilter, typeFilter, advocateFilter, stageFilter, yearFilter]);

  const heldDate = dateMode === "single" ? selectedDate : startDate;
  const dateSummary =
    dateMode === "single"
      ? fmtLong(selectedDate)
      : `${fmtLong(startDate)} – ${fmtLong(endDate)}`;

  // ---- exporters (reuse original PDF/CSV, add Excel; return a Blob) ----
  const addHeader = (doc, title) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, 25, 196, 25);
    doc.setFontSize(12);
  };
  const listTitle = `Cause List ${dateMode === "single" ? "for " + dateSummary : dateSummary}`;

  const buildPdfBlob = () => {
    const doc = new jsPDF();
    addHeader(doc, listTitle);
    const cfg = { leftMargin: 15, topPadding: 12, bottomPadding: 12, columnWidth: 70, columnGap: 20, lineHeight: 5, fieldSpacing: 4, spaceBetweenBoxes: 8 };
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 45;
    filteredCases.forEach((item, index) => {
      const left = [
        ["CASE NO:", item.caseNo || "N/A"],
        ["PREVIOUS HEARING DATE:", item.previousHearingDate || "N/A"],
        ["CASE TYPE:", item.caseType || "N/A"],
        ["COURT NAME:", item.courtName || item.court || "N/A"],
        ["NEXT HEARING DATE:", item.nextHearingDate || "N/A"],
      ];
      const right = [
        ["PETITIONER NAME:", item.petitionerName || "N/A"],
        ["RESPONDENT NAME:", item.respondentName || "N/A"],
      ];
      const heights = [left, right].map((col) =>
        col.reduce((acc, [label, value]) => {
          const lh = doc.getTextDimensions(doc.splitTextToSize(label, cfg.columnWidth)).h;
          const vh = doc.getTextDimensions(doc.splitTextToSize(String(value), cfg.columnWidth)).h;
          return acc + lh + vh + cfg.fieldSpacing;
        }, 0)
      );
      const boxHeight = Math.max(...heights) + cfg.topPadding + cfg.bottomPadding - cfg.fieldSpacing;
      const needed = boxHeight + (index < filteredCases.length - 1 ? cfg.spaceBetweenBoxes : 0);
      if (y + needed > pageHeight - 20) { doc.addPage(); addHeader(doc, listTitle); y = 45; }
      doc.rect(cfg.leftMargin - 2, y - cfg.topPadding, 180, boxHeight);
      [left, right].forEach((col, ci) => {
        const x = cfg.leftMargin + (ci === 0 ? 0 : cfg.columnWidth + cfg.columnGap);
        let yc = y;
        col.forEach(([label, value], fi) => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          const ll = doc.splitTextToSize(label, cfg.columnWidth);
          doc.text(ll, x, yc);
          yc += doc.getTextDimensions(ll).h || ll.length * cfg.lineHeight;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          const vl = doc.splitTextToSize(String(value), cfg.columnWidth);
          doc.text(vl, x, yc);
          yc += doc.getTextDimensions(vl).h || vl.length * cfg.lineHeight;
          if (fi < col.length - 1) yc += cfg.fieldSpacing;
        });
      });
      y += boxHeight + cfg.spaceBetweenBoxes;
    });
    return { blob: doc.output("blob"), ext: "pdf" };
  };

  const ROWS = () =>
    filteredCases.map((c, i) => [
      i + 1,
      c.caseNo || "N/A",
      [c.petitionerName, c.respondentName].filter(Boolean).join(" vs ") || "N/A",
      c.petitionerName || "N/A",
      c.respondentName || "N/A",
      c.courtName || c.court || "N/A",
      c.nextHearingDate || "N/A",
    ]);
  const HEADERS = ["S.No.", "Case No.", "Case Title", "Petitioner", "Respondent", "Court", "Hearing Date"];

  const buildCsvBlob = () => {
    const rows = [HEADERS.join(",")];
    ROWS().forEach((r) => rows.push(r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
    return { blob: new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" }), ext: "csv" };
  };

  const buildExcelBlob = () => {
    const head = `<tr>${HEADERS.map((h) => `<th style="background:#e7e6e6;border:1px solid #000;padding:6px;">${escapeHtml(h)}</th>`).join("")}</tr>`;
    const body = ROWS()
      .map((r) => `<tr>${r.map((v) => `<td style="border:1px solid #000;padding:6px;">${escapeHtml(v)}</td>`).join("")}</tr>`)
      .join("");
    const html = `<html><head><meta charset="utf-8"></head><body><table border="1" style="border-collapse:collapse;font-family:Arial;font-size:11px;">${head}${body}</table></body></html>`;
    return { blob: new Blob([html], { type: "application/vnd.ms-excel" }), ext: "xls" };
  };

  const handleExport = () => {
    if (!filteredCases.length) { toast.error("No cases to export."); return; }
    setExporting(true);
    try {
      const build = format === "pdf" ? buildPdfBlob : format === "csv" ? buildCsvBlob : buildExcelBlob;
      const { blob, ext } = build();
      const dLabel =
        dateMode === "single" && selectedDate
          ? selectedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "")
          : "Range";
      const courtLabel = courtFilter !== "all" ? "_" + courtFilter.replace(/[^\w]+/g, "") : "";
      const name = `CauseList_${dLabel}${courtLabel}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setExportedFile({ name, sizeKB: Math.max(1, Math.round(blob.size / 1024)), url, format });
      setStep(5);
      toast.success("Cause list exported successfully.");
    } catch (e) {
      console.error("export:", e);
      toast.error("Failed to export cause list.");
    } finally {
      setExporting(false);
    }
  };

  // ---- navigation ----
  const goNext = async () => {
    if (step === 1) {
      if (dateMode === "single" && !selectedDate) return toast.error("Please select a date.");
      if (dateMode === "range" && (!startDate || !endDate)) return toast.error("Please select a start and end date.");
      const result = await loadCases();
      if (result.length === 0)
        toast("No cases have a hearing on this selection — try another date or a Date Range.", { icon: "⚠️" });
      setStep(2);
    } else if (step === 3) {
      if (!filteredCases.length) return toast.error("No cases match — adjust the date or filters.");
      setStep(4);
    } else {
      setStep((s) => Math.min(4, s + 1));
    }
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const resetAll = () => {
    setStep(1);
    setCases([]);
    setSelectedDate(null);
    setStartDate(null);
    setEndDate(null);
    setCourtFilter("all");
    setTypeFilter("all");
    setAdvocateFilter("all");
    setStageFilter("all");
    setYearFilter("all");
    setExportedFile(null);
  };

  // ---- shared styles ----
  const labelCls = "block text-sm font-medium text-slate-600 mb-1.5";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";
  const SelectField = ({ value, onChange, children }) => (
    <div className="relative">
      <SelectMenu chevron={false} value={value} onChange={(e) => onChange(e.target.value)} className={`${fieldCls} appearance-none pr-8`}>
        {children}
      </SelectMenu>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
  const sideCard = "rounded-2xl border border-slate-200 bg-slate-50 p-5";
  const detailRow = (label, value) => (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-right text-sm font-medium text-slate-800">{value || "—"}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-slate-500">Reports &amp; Insights</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Export Cause List</span>
        </div>

        {step !== 5 && (
          <>
            <div className="mt-2">
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Export Cause List</h1>
              <p className="mt-0.5 text-xs text-slate-500">Export cause list based on hearing dates</p>
            </div>

            {/* Stepper */}
            <div className="mt-6 flex flex-wrap items-center gap-y-3">
              {STEPS.map((s, i) => {
                const done = step > s.n;
                const active = step === s.n;
                return (
                  <div key={s.n} className="flex items-center">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                        active ? "bg-yellow-500" : done ? "bg-emerald-500" : "border-2 border-slate-300 bg-white text-slate-400"
                      }`}
                      style={active || done ? { color: "#ffffff" } : undefined}
                    >
                      {done ? <Check className="h-4 w-4" /> : s.n}
                    </span>
                    <span className={`ml-2 whitespace-nowrap text-sm font-medium ${active ? "text-slate-900" : "text-slate-400"}`}>
                      {s.label}
                    </span>
                    {i < STEPS.length - 1 && <span className="mx-3 hidden h-px w-10 border-t border-dashed border-slate-300 sm:block" />}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ===== STEP 1 — Select Date ===== */}
        {step === 1 && (
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800">Select Selection</h3>
              <div className="mt-4 flex items-center gap-8">
                {[{ k: "single", l: "Single Date" }, { k: "range", l: "Date Range" }].map((o) => (
                  <label key={o.k} className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                    <input type="radio" name="dateMode" checked={dateMode === o.k} onChange={() => setDateMode(o.k)} className="h-4 w-4 accent-blue-600" />
                    {o.l}
                  </label>
                ))}
              </div>

              {dateMode === "single" ? (
                <div className="mt-5">
                  <label className={labelCls}>Select Date <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <DatePicker
                      selected={selectedDate}
                      onChange={setSelectedDate}
                      dateFormat="dd MMM yyyy"
                      placeholderText="Select a date"
                      className={`${fieldCls} pr-9`}
                    />
                    <CalendarIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>From <span className="text-red-500">*</span></label>
                    <DatePicker selected={startDate} onChange={setStartDate} dateFormat="dd MMM yyyy" placeholderText="Start date" className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>To <span className="text-red-500">*</span></label>
                    <DatePicker selected={endDate} onChange={setEndDate} dateFormat="dd MMM yyyy" placeholderText="End date" minDate={startDate} className={fieldCls} />
                  </div>
                </div>
              )}
            </div>

            <div className="flex">
              <div className={`${sideCard} flex w-full items-center gap-4`}>
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
                  <ClipboardList className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-xs text-slate-400">You are exporting cause list for</p>
                  <p className="mt-0.5 text-lg font-bold text-slate-900">{heldDate ? fmtLong(heldDate) : "Select a date"}</p>
                  {heldDate && <p className="text-sm text-slate-500">({fmtWeekday(heldDate)})</p>}
                  {dateMode === "range" && endDate && <p className="mt-0.5 text-sm text-slate-500">to {fmtLong(endDate)}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 2 — Filters ===== */}
        {step === 2 && (
          <>
          {cases.length === 0 && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              No cases have a hearing on <span className="font-semibold">{dateSummary}</span>, so there's nothing to filter. Go <span className="font-semibold">Back</span> to pick another date, or switch to a <span className="font-semibold">Date Range</span>.
            </div>
          )}
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div>
                <label className={labelCls}>Court</label>
                <SelectField value={courtFilter} onChange={setCourtFilter}>
                  <option value="all">All Courts</option>
                  {courtOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </SelectField>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Case Type</label>
                <SelectField value={typeFilter} onChange={setTypeFilter}>
                  <option value="all">All Types</option>
                  {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </SelectField>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Advocate</label>
                <SelectField value={advocateFilter} onChange={setAdvocateFilter}>
                  <option value="all">All Advocates</option>
                  {advocateOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                </SelectField>
              </div>

              {showMore && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Case Stage</label>
                    <SelectField value={stageFilter} onChange={setStageFilter}>
                      <option value="all">All Stages</option>
                      {stageOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </SelectField>
                  </div>
                  <div>
                    <label className={labelCls}>Case Year</label>
                    <SelectField value={yearFilter} onChange={setYearFilter}>
                      <option value="all">All Years</option>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </SelectField>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowMore((v) => !v)}
                className="mt-5 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <SlidersHorizontal className="h-4 w-4 text-slate-400" /> {showMore ? "Fewer Filters" : "More Filters"}
              </button>
            </div>

            <div className="flex">
              <div className={`${sideCard} w-full`}>
                <h4 className="text-sm font-bold text-slate-800">Selected Details</h4>
                <div className="mt-3 divide-y divide-slate-200">
                  {detailRow("Date", dateSummary)}
                  {detailRow("Court", courtFilter === "all" ? "All Courts" : courtFilter)}
                  {detailRow("Case Type", typeFilter === "all" ? "All Types" : typeFilter)}
                  {detailRow("Advocate", advocateFilter === "all" ? "All Advocates" : advocateFilter)}
                  {showMore && detailRow("Stage", stageFilter === "all" ? "All Stages" : stageFilter)}
                  {showMore && detailRow("Year", yearFilter === "all" ? "All Years" : yearFilter)}
                  {detailRow("Matching Cases", String(filteredCases.length))}
                </div>
              </div>
            </div>
          </div>
          </>
        )}

        {/* ===== STEP 3 — Preview ===== */}
        {step === 3 && (
          <div className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Preview Cause List</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  Date: {dateSummary}
                  {courtFilter !== "all" ? `  |  Court: ${courtFilter}` : ""}
                  {typeFilter !== "all" ? `  |  Type: ${typeFilter}` : ""}
                </p>
              </div>
              <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700">
                Total Cases: {filteredCases.length}
              </span>
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3">S.No.</th>
                      <th className="px-4 py-3">Case No.</th>
                      <th className="px-4 py-3">Case Title</th>
                      <th className="px-4 py-3">Petitioner / Respondent</th>
                      <th className="px-4 py-3">Court</th>
                      <th className="px-4 py-3">Hearing Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredCases.map((c, i) => (
                      <tr key={c.id || i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{c.caseNo || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          {[c.petitionerName, c.respondentName].filter(Boolean).join(" vs ") || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{c.petitionerName || c.respondentName || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{c.courtName || c.court || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">{c.nextHearingDate || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredCases.length === 0 && (
                <p className="py-12 text-center text-sm text-slate-500">No cases for this selection.</p>
              )}
            </div>
          </div>
        )}

        {/* ===== STEP 4 — Export format ===== */}
        {step === 4 && (
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h3 className="text-sm font-bold text-slate-800">Choose Export Format</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {FORMATS.map((f) => {
                  const active = format === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFormat(f.key)}
                      className={`flex flex-col items-center rounded-2xl border-2 bg-white p-6 text-center shadow-sm transition-colors ${
                        active ? "border-yellow-400 ring-2 ring-yellow-400/30" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className={`flex h-14 w-14 items-center justify-center rounded-xl ${f.tint}`}>
                        <f.Icon className="h-6 w-6" />
                      </span>
                      <span className="mt-3 text-base font-bold text-slate-900">{f.label}</span>
                      <span className="mt-1 text-xs text-slate-500">{f.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={sideCard}>
              <h4 className="text-sm font-bold text-slate-800">Export Summary</h4>
              <div className="mt-3 divide-y divide-slate-200">
                {detailRow("Date", dateSummary)}
                {detailRow("Court", courtFilter === "all" ? "All Courts" : courtFilter)}
                {detailRow("Case Type", typeFilter === "all" ? "All Types" : typeFilter)}
                {detailRow("Advocate", advocateFilter === "all" ? "All Advocates" : advocateFilter)}
                {detailRow("Total Cases", String(filteredCases.length))}
                {detailRow("Format", FORMATS.find((f) => f.key === format)?.label)}
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 5 — Success ===== */}
        {step === 5 && exportedFile && (
          <div className="mx-auto mt-10 max-w-xl text-center">
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
              <CheckCircle2 className="h-10 w-10" />
            </span>
            <h2 className="mt-5 text-xl font-bold text-slate-900">Export Successful!</h2>
            <p className="mt-1 text-sm text-slate-500">Your cause list has been exported successfully.</p>

            <div className="mt-6 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-grow">
                <p className="truncate text-sm font-semibold text-slate-800">{exportedFile.name}</p>
                <p className="text-xs text-slate-400">{exportedFile.format.toUpperCase()} · {exportedFile.sizeKB} KB</p>
              </div>
              <a href={exportedFile.url} download={exportedFile.name} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600" title="Download">
                <Download className="h-5 w-5" />
              </a>
              <a href={exportedFile.url} target="_blank" rel="noopener noreferrer" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Open">
                <Eye className="h-5 w-5" />
              </a>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3">
              <button type="button" onClick={resetAll} className={ACTION_BTN_WHITE}>
                Export Another
              </button>
              <button type="button" onClick={() => navigate("/portal/dashboard")} className={ACTION_BTN_GOLD}>
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* ===== Footer nav (steps 1-4) ===== */}
        {step <= 4 && (
          <div className="mt-8 flex items-center justify-between">
            {step === 1 ? (
              <button type="button" onClick={() => navigate(-1)} className={ACTION_BTN_WHITE}>
                <ChevronLeft className={ACTION_BTN_ICON} /> Cancel
              </button>
            ) : (
              <button type="button" onClick={goBack} className={ACTION_BTN_WHITE}>
                <ChevronLeft className={ACTION_BTN_ICON} /> Back
              </button>
            )}

            {step < 4 ? (
              <button type="button" disabled={isLoading} onClick={goNext} className={`${ACTION_BTN_GOLD} disabled:opacity-50`}>
                {isLoading ? <Loader2 className={`${ACTION_BTN_ICON} animate-spin`} /> : null} Next
              </button>
            ) : (
              <button type="button" disabled={exporting} onClick={handleExport} className={`${ACTION_BTN_GOLD} disabled:opacity-50`}>
                {exporting ? <Loader2 className={`${ACTION_BTN_ICON} animate-spin`} /> : <Download className={ACTION_BTN_ICON} />} Export Now
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ExportCauseList;
